// ═══════════════════════════════════════════════════
// API Client — Fetch wrapper with auth, token refresh, retry
// "Trust the data, verify the scan"
// ═══════════════════════════════════════════════════

import type { ApiError } from '@/types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

// ── Token Management ─────────────────────────────
// Decoupled from Zustand to avoid circular deps.
// The auth store registers itself on boot.

type TokenGetter = () => string | null;
type TokenSetter = (access: string, refresh: string) => void;
type LogoutFn = () => void;

let getAccessToken: TokenGetter = () => null;
let getRefreshToken: TokenGetter = () => null;
let setTokens: TokenSetter = () => {};
let triggerLogout: LogoutFn = () => {};

export function registerAuthHandlers(
  accessGetter: TokenGetter,
  refreshGetter: TokenGetter,
  tokenSetter: TokenSetter,
  logout: LogoutFn,
) {
  getAccessToken = accessGetter;
  getRefreshToken = refreshGetter;
  setTokens = tokenSetter;
  triggerLogout = logout;
}

// ── Refresh Lock ─────────────────────────────────
let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) return false;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });

    if (!res.ok) return false;

    const data = await res.json();
    setTokens(data.accessToken, data.refreshToken);
    return true;
  } catch {
    return false;
  }
}

async function ensureValidToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = refreshAccessToken();
  const result = await refreshPromise;
  refreshPromise = null;
  return result;
}

// ── API Client ───────────────────────────────────

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    params?: Record<string, string>,
    retried = false,
  ): Promise<T> {
    let url: string;
    if (params) {
      const urlObj = new URL(`${this.baseUrl}${path}`, window.location.origin);
      Object.entries(params).forEach(([key, value]) =>
        urlObj.searchParams.append(key, value),
      );
      url = urlObj.toString();
    } else {
      url = `${this.baseUrl}${path}`;
    }

    const response = await fetch(url, {
      method,
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    // Handle 401: try refresh once, then retry
    if (response.status === 401 && !retried) {
      const refreshed = await ensureValidToken();
      if (refreshed) {
        return this.request<T>(method, path, body, params, true);
      }
      triggerLogout();
      throw createApiError(401, 'Session expired. Please log in again.');
    }

    if (response.status === 401) {
      triggerLogout();
      throw createApiError(401, 'Session expired. Please log in again.');
    }

    if (!response.ok) {
      let error: ApiError;
      try {
        error = await response.json();
      } catch {
        error = createApiError(response.status, `Request failed with status ${response.status}`);
      }
      throw error;
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    return this.request<T>('GET', path, undefined, params);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  /**
   * Create an SSE connection for live scan progress.
   */
  createScanStream(scanId: string): EventSource {
    const token = getAccessToken();
    const url = `${this.baseUrl}/scans/${scanId}/stream?token=${encodeURIComponent(token || '')}`;
    return new EventSource(url);
  }
}

function createApiError(status: number, message: string): ApiError {
  return {
    error: status === 401 ? 'Unauthorized' : 'Request Failed',
    message,
    statusCode: status,
  };
}

export const api = new ApiClient(API_BASE);
